import React, { useMemo } from "react";
import {
  Modal,
  Tabs,
  Typography,
  Avatar,
  Tag,
  Skeleton,
  Result,
  Descriptions,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../../i18n/formatos";
import { TherapistPatientEvaluationsTab } from "../../PatientCmp/TherapistPatientEvaluationsTab";
import { TherapistPatientAnamnesisTab } from "../../PatientCmp/TherapistPatientAnamnesisTab";
import { TherapistPatientMotricityTab } from "../../PatientCmp/TherapistPatientMotricityTab";
import "./TherapistPatientModal.scss";

const { Text, Title } = Typography;

function calcAge(birthdate) {
  if (!birthdate) return null;
  const d = new Date(birthdate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

// El sexo es un valor cerrado de la BD, asi que su etiqueta SI es interfaz.
function sexoLabel(sexo, t) {
  if (sexo === "M") return t("sexo.hombre");
  if (sexo === "F") return t("sexo.mujer");
  if (sexo === "O") return t("sexo.otro");
  return "—";
}

export function TherapistPatientModal({
  open,
  onClose,
  patient,
  loading,
  error,
  activeTab,
  onTabChange,
  onClinicalInfoUpdate,
}) {
  // "Nombre" y "Apellidos" salen del bloque comun.
  const { t } = useTranslation(["patient", "common"]);
  const { fecha } = useFormatos();

  const fullName = useMemo(() => {
    const n = `${patient?.name || ""} ${patient?.surname || ""}`.trim();
    return n || t("ficha.nombrePorDefecto");
  }, [patient?.name, patient?.surname, t]);

  const age = useMemo(() => calcAge(patient?.birthdate), [patient?.birthdate]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnHidden
      className="revi-therapist-patient-modal"
    >
      <div className="revi-therapist-patient-modal__header">
        <Avatar size={64} src={patient?.avatar} icon={<UserOutlined />} />
        <div className="revi-therapist-patient-modal__title">
          <Title level={4} style={{ margin: 0 }}>
            {fullName}
          </Title>

          <div className="revi-therapist-patient-modal__badges">
            <Tag variant="filled">{sexoLabel(patient?.sexo, t)}</Tag>
            <Tag variant="filled">
              {/* Contador: en frances el cero y el uno van en singular. */}
              {age !== null
                ? t("common:contadores.anios", { count: age })
                : t("ficha.edadDesconocida")}
            </Tag>
          </div>
          <Text type="secondary">{t("ficha.subtitulo")}</Text>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          {
            key: "detail",
            label: t("ficha.tabs.detalle"),
            children: (
              <div className="revi-therapist-patient-modal__body">
                {loading ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : error ? (
                  <Result
                    status="error"
                    title={t("ficha.errorCarga")}
                    subTitle={error}
                  />
                ) : (
                  <Descriptions bordered column={1}>
                    <Descriptions.Item label={t("common:fields.name")}>
                      {patient?.name || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("common:fields.surname")}>
                      {patient?.surname || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("ficha.campos.sexo")}>
                      {sexoLabel(patient?.sexo, t)}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("ficha.campos.fechaNacimiento")}
                    >
                      {fecha(patient?.birthdate)}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </div>
            ),
          },
          {
            key: "anamnesis",
            label: t("ficha.tabs.anamnesis"),
            children: (
              <div className="revi-therapist-patient-modal__body">
                {loading ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : (
                  <TherapistPatientAnamnesisTab
                    patient={patient}
                    onClinicalInfoUpdate={onClinicalInfoUpdate}
                  />
                )}
              </div>
            ),
          },
          {
            key: "evaluations",
            label: t("ficha.tabs.evaluaciones"),
            children: (
              <div className="revi-therapist-patient-modal__body">
                <TherapistPatientEvaluationsTab
                  patientId={patient?._id}
                  active={activeTab === "evaluations"}
                />
              </div>
            ),
          },
          {
            key: "motricity",
            label: t("ficha.tabs.motricidad"),
            children: (
              <div className="revi-therapist-patient-modal__body">
                <TherapistPatientMotricityTab
                  patientId={patient?._id}
                  active={activeTab === "motricity"}
                />
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}
